import type { FunctionComponent } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
// dotLottie-web renders .lottie packages onto a <canvas>. The payload is
// base64-inlined at build time (see vite.config.ts) so the distributable
// bundle stays a single file with no sibling asset hosting.
import { DotLottie } from '@lottiefiles/dotlottie-web';

import smileToCaptureSrc from './smile_to_capture_animation.lottie';

import smileInstructionSrc from './smile_to_capture_instruction.lottie';

/**
 * Smile-flow artwork (a face breaking into a smile), backing both
 * smile-specific surfaces from the v12 prototype:
 *   - the guidelines hero (`SmileHero`) — fills the `.hero-oval` frame so
 *     smile users get a smile cue instead of the face-directions animation
 *     (which was misleading them into turning their head);
 *   - the "Please smile…" capture overlay (`SmileGuideOverlay`) — a
 *     translucent guide drawn over the live camera feed while we wait for the
 *     smile, mirroring ActiveLivenessOverlay's role in strict mode.
 *
 * Design ships one asset per surface — `smile_to_capture_instruction.lottie`
 * and `smile_to_capture_animation.lottie`. They wrap the SAME character
 * precomp and differ only in its baked opacity: 100% for the opaque hero, 75%
 * for the camera overlay. Opacity therefore belongs to the asset, NOT to CSS —
 * an `opacity` on the overlay would multiply with the baked 75% and dim it
 * further than design intends.
 *
 * The Figma prototype's guidelines hero also draws a green oval in around the
 * face, growing from the chin up both sides as the face smiles. The supplied
 * `.lottie` has no such layer (confirmed: only `mouth`/`nose`/`eyebrows`/
 * `eyes`/`head base`/`ear`/`torso`) — it's synthesised here as an SVG stroke
 * reveal driven by the face animation's own playback progress, rather than a
 * second hand-authored asset. Timing/easing are approximated from the
 * prototype screenshots, not exact per-frame keyframes from design.
 */

// Shared player. Both surfaces run the same looping character animation; they
// differ in which asset supplies it (baked opacity) and in how they're framed,
// which the caller supplies via src + className + style.
const SmileLottie: FunctionComponent<{
  src: string;
  className: string;
  style?: Record<string, string | number>;
}> = ({ src, className, style }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<DotLottie | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return undefined;
    const anim = new DotLottie({
      canvas: canvasRef.current,
      src,
      loop: true,
      autoplay: true,
      // Source lottie is 240f @ 30fps = 8s.
      speed: 1.5,
      renderConfig: { autoResize: true },
    });
    animRef.current = anim;
    return () => {
      anim.destroy();
      animRef.current = null;
    };
  }, [src]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
      style={style}
    />
  );
};

// The artwork sits high inside its own frame, so centring the canvas on the
// oval leaves the face visibly above centre. The asset's `torso` layer is
// positioned outside the 163×206 composition viewport and never draws, so the
// space it reserves below the chin stays empty: measured against the rendered
// canvas, the visible head+ears span 15.5% → 74% of canvas height, putting the
// artwork's centre at 0.446 rather than 0.5. Nudging the canvas down by that
// 5.4% deficit centres the face in the oval. A percentage (not px) keeps it
// correct across the `.hero-oval` clamp range, since translate percentages
// resolve against the canvas's own height.
//
// This is a workaround for the asset's framing, not a layout constant — if
// design re-exports with the character centred (or the torso inside the
// viewport), reset this to -50%.
const ARTWORK_Y_CORRECTION = '-44.6%';

// Half-arcs of the guide oval. The viewBox aspect ratio (110×130 ≈ 0.846)
// matches the `.hero-oval` box (`clamp(135px,22dvh,195px)` ×
// `clamp(160px,26dvh,230px)`), so with the default uniform `meet` scaling the
// ellipse fills the box and the green outline sits on top of the grey CSS
// border. (Uniform scaling matters: `preserveAspectRatio="none"` /
// `vector-effect="non-scaling-stroke"` break the `pathLength`-based dash
// reveal below — the front then draws from the wrong end — so we keep uniform
// scaling and a scaling stroke instead.) Both halves start at the bottom
// (chin) point: with `pathLength=1` + `stroke-dasharray="1 1"` + an animated
// `stroke-dashoffset` (1 → 0), each reveals from its start point forward —
// i.e. from the chin up toward the top on both sides at once, per the
// prototype. The ellipse is inset ~1.5 units from the box edge so the stroke
// isn't clipped by the oval's `overflow: hidden`.
const OVAL_CX = 55;
const OVAL_CY = 65;
const OVAL_RX = 53.5;
const OVAL_RY = 63.5;
const OVAL_CHIN = `${OVAL_CX} ${OVAL_CY + OVAL_RY}`;
const OVAL_TOP = `${OVAL_CX} ${OVAL_CY - OVAL_RY}`;
const OVAL_RIGHT_HALF = `M ${OVAL_CHIN} A ${OVAL_RX} ${OVAL_RY} 0 0 0 ${OVAL_TOP}`;
const OVAL_LEFT_HALF = `M ${OVAL_CHIN} A ${OVAL_RX} ${OVAL_RY} 0 0 1 ${OVAL_TOP}`;

// Staged reveal, choreographed to the smile lottie's OWN beats so the oval
// reacts to the face. Source beats (frame of 240; op=240 @ 30fps):
//   • eye blink #1  63–69      • smile forms 96 / releases 141
//   • eye blink #2  158–164
// Sequence (matches design): draw the bottom "smile" arc → HOLD until the
// face smiles (96) → grow up both sides → HOLD at the horseshoe until the
// second blink (~161) → close at the top → stay full until the loop resets.
// The smile lottie is NEVER paused — only the oval's reveal plateaus. `[p, r]`
// pairs map normalized playback (`currentFrame / totalFrames`) to each
// half-arc's draw fraction (`r`, 0 → 1 from the chin); equal consecutive `r`
// values are the holds. Frame anchors are written as `frame/240` so they read
// against the beats above.
const TOTAL_FRAMES = 240;
const at = (frame: number): number => frame / TOTAL_FRAMES;
const REVEAL_KEYFRAMES: Array<[number, number]> = [
  [0.0, 0.0],
  [at(60), 0.22], // draw the bottom "smile" arc
  [at(138), 0.22], // HOLD while the face breaks into a smile (mouth morphs 96→141)
  [at(156), 0.82], // grow up both sides to the horseshoe
  [at(164), 0.82], // HOLD → released just after the second blink (158–164)
  [at(192), 1.0], // close at the top
  [1.0, 1.0], // stay full until the loop resets
];

// Piecewise-linear lookup over REVEAL_KEYFRAMES (ascending p).
const revealForProgress = (p: number): number => {
  const kfs = REVEAL_KEYFRAMES;
  if (p <= kfs[0][0]) return kfs[0][1];
  for (let i = 1; i < kfs.length; i += 1) {
    const [p1, r1] = kfs[i];
    if (p <= p1) {
      const [p0, r0] = kfs[i - 1];
      const span = p1 - p0;
      const t = span > 0 ? (p - p0) / span : 1;
      return r0 + (r1 - r0) * t;
    }
  }
  return kfs[kfs.length - 1][1];
};

/**
 * Guidelines hero for smile mode: the smile animation sized to fill the
 * existing `.hero-oval` frame, with a green guide oval that draws in from the
 * chin in staged steps as the face's own playback advances (see the module
 * doc above — the oval isn't part of the supplied `.lottie`, so it's synced
 * here via the animation's `frame` event and shaped by `REVEAL_KEYFRAMES`
 * rather than baked into the asset).
 */
export const SmileHero: FunctionComponent = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rightArcRef = useRef<SVGPathElement>(null);
  const leftArcRef = useRef<SVGPathElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return undefined;
    const anim = new DotLottie({
      canvas: canvasRef.current,
      // Fully opaque variant — the hero fills its own frame, with no camera
      // feed to show through.
      src: smileInstructionSrc,
      loop: true,
      autoplay: true,
      // Source lottie is 240f @ 30fps = 8s. The oval reveal below is
      // driven off currentFrame so it stays in sync at any speed.
      speed: 1.5,
      renderConfig: { autoResize: true },
    });

    // Direct DOM writes (not Preact state) so a ~30fps event doesn't trigger
    // a re-render of the tree on every tick — only the two path elements'
    // dashoffset changes.
    const onFrame = (event: { currentFrame: number }) => {
      // Normalize against the known composition length (TOTAL_FRAMES), not
      // anim.totalFrames, so the reveal's frame anchors (smile 96, blink2 161)
      // line up exactly with the lottie's own beats. `speed` scales wall-clock
      // time but not currentFrame's 0..TOTAL_FRAMES range, so this stays valid.
      const progress = Math.min(
        1,
        Math.max(0, event.currentFrame / TOTAL_FRAMES),
      );
      const reveal = revealForProgress(progress);
      const offset = String(1 - reveal);
      if (rightArcRef.current) {
        rightArcRef.current.style.strokeDashoffset = offset;
      }
      if (leftArcRef.current) {
        leftArcRef.current.style.strokeDashoffset = offset;
      }
    };
    anim.addEventListener('frame', onFrame);

    return () => {
      anim.removeEventListener('frame', onFrame);
      anim.destroy();
    };
  }, []);

  // Wrapper fills the `.hero-oval` box (100%) and anchors both layers: the
  // face canvas overflows to 120% and is clipped by the oval (matching
  // HeroAnimation's sizing), while the green guide oval overlays the box 1:1
  // so it lands on the grey border.
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <canvas
        ref={canvasRef}
        className="smile-hero"
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, ${ARTWORK_Y_CORRECTION})`,
          width: '120%',
          height: '120%',
          display: 'block',
        }}
      />
      <svg
        className="smile-hero-oval"
        viewBox="0 0 110 130"
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        <path
          ref={rightArcRef}
          d={OVAL_RIGHT_HALF}
          pathLength={1}
          stroke="#1DB954"
          stroke-width="2.5"
          stroke-linecap="round"
          fill="none"
          style={{ strokeDasharray: '1 1', strokeDashoffset: 1 }}
        />
        <path
          ref={leftArcRef}
          d={OVAL_LEFT_HALF}
          pathLength={1}
          stroke="#1DB954"
          stroke-width="2.5"
          stroke-linecap="round"
          fill="none"
          style={{ strokeDasharray: '1 1', strokeDashoffset: 1 }}
        />
      </svg>
    </div>
  );
};

/**
 * "Please smile…" capture overlay: the smile animation drawn over the camera
 * feed so the user's real face stays visible underneath. The see-through comes
 * from the asset itself (`character @ 75%`), so no CSS `opacity` here — see the
 * module doc. Rendered through CameraPreview's `overlay` slot. The animation's
 * native portrait ratio (163×206) is preserved so the face isn't distorted.
 */
export const SmileGuideOverlay: FunctionComponent = () => (
  <SmileLottie
    src={smileToCaptureSrc}
    className="smile-guide-overlay"
    style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '78%',
      aspectRatio: '163 / 206',
      pointerEvents: 'none',
      zIndex: 2,
      display: 'block',
    }}
  />
);
